/**
 * Barra de reportes imprimibles.
 *
 * Dibuja un botón «Imprimir» por cada reporte PDF que el modelo declara
 * (`spec.reports`, con su `enabled` YA EVALUADO por el backend). Al apretarlo
 * descarga `GET /api/v1/model/<modelo>/<id>/print/<reporte>/` y dispara la
 * descarga del archivo en el navegador.
 *
 * Mismas reglas que la barra de acciones, para que las dos se comporten igual:
 *
 * 1. **`enabled` llega evaluado, y lo que no se puede NO se dibuja.** Un
 *    comprobante en borrador no se imprime, y es el backend el que lo dice
 *    (dominio de disponibilidad del reporte); acá sólo se obedece, y se obedece
 *    con el mismo criterio que la botonera de acciones —ver `noSeDibuja` en
 *    `ActionBar`—: un botón «Imprimir» en gris que no se va a poder apretar es
 *    ruido, y el manual (01 §1.4) promete que arriba están sólo los botones que
 *    se pueden usar ahora. Antes esto los dibujaba deshabilitados y la barra de
 *    al lado los escondía: dos criterios en la misma fila de botones.
 * 2. **Degrada a nada.** Sin `spec.reports` —o sin registro— no dibuja nada.
 *    Ninguna pantalla depende de que exista.
 * 3. **El nombre del archivo lo pone el backend** (cabecera `Content-Disposition`:
 *    «Factura FV-VTA-00000001.pdf»). El cliente no lo reinventa.
 * 4. **`blockedReason` sí se muestra en gris**: que haya cambios sin guardar no
 *    es un estado del registro, es una condición de la pantalla que se levanta
 *    en un clic. Esconder el botón ahí dejaría a la persona buscándolo.
 */

import { useMutation } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { notify } from '@/components/shell/toast';
import { api } from '@/lib/api';
import type { ModelAction, ModelReport, ModelSpec } from '@/lib/types';
import { ActionDialog, type ActionParamValues } from '@/views/ActionDialog';
import {
  DETALLE_SUCIO,
  motivoDelServidor,
  salidaDeGuardado,
  type AccionNoDisponible,
  type EstadoDeGuardado,
} from '@/views/MotivoBloqueo';
import { Toolbar, type ToolbarItem } from '@/views/Toolbar';

export interface ReportBarProps {
  spec: ModelSpec;
  /** Registro sobre el que se genera el reporte. */
  recordId: number;
  size?: 'sm' | 'md';
  /**
   * Motivo por el que la impresión está bloqueada ahora mismo (típicamente: hay
   * cambios sin guardar). El reporte corre sobre lo que hay en la base, no sobre
   * lo que se ve en pantalla, así que con cambios pendientes conviene frenarlo.
   */
  blockedReason?: string | null;
  /** El guardado de la ficha, para ofrecer la salida en el mismo clic. */
  guardado?: EstadoDeGuardado | null;
}

/**
 * Dispara la descarga de un blob en el navegador con el nombre que trae el
 * backend. Se hace con un `<a download>` efímero y una object URL que se libera
 * enseguida: dejarla viva retiene el blob entero en memoria.
 */
export function guardarArchivo(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = filename;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  // El revoke se demora un tick: algunos navegadores cancelan la descarga si la
  // URL se libera en el mismo turno del click.
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

/**
 * Un reporte con parámetros se pide con el MISMO diálogo que una acción de
 * servidor con parámetros, así que se lo disfraza de acción.
 *
 * No es un truco: un `ActionParam` es un `ActionParam` venga de donde venga, y
 * el diálogo sólo mira `label`, `params` y `help`. La alternativa —un segundo
 * diálogo casi igual— garantizaba que a uno de los dos se le olvidara validar lo
 * obligatorio.
 */
function comoAccion(reporte: ModelReport): ModelAction {
  return {
    name: reporte.name,
    label: reporte.label,
    params: reporte.params ?? [],
    help: reporte.help ?? '',
    confirm: false,
  };
}

/**
 * Los botones de impresión DESCRITOS, para que la cabecera del formulario los
 * pueda meter en la misma botonera plegable que las acciones de servidor y el
 * correo. Ver `Toolbar` y `useActionItems`.
 */
export function useReportItems({
  spec,
  recordId,
  blockedReason = null,
  guardado = null,
}: Omit<ReportBarProps, 'size'>): {
  items: ToolbarItem[];
  dialogs: ReactNode;
  ocultas: AccionNoDisponible[];
} {
  // Reporte esperando que el usuario complete sus parámetros (el rango de fechas
  // de un libro de IVA). `null` = no hay diálogo abierto.
  const [enDialogo, setEnDialogo] = useState<ModelReport | null>(null);

  const imprimir = useMutation({
    mutationFn: (entrada: { reporte: ModelReport; params: ActionParamValues }) =>
      api.downloadReport(spec.model, recordId, entrada.reporte.name, entrada.params),
    onSuccess: ({ blob, filename }) => {
      setEnDialogo(null);
      guardarArchivo(blob, filename);
    },
    onError: (error) => {
      notify.error('No se pudo generar el PDF', error instanceof Error ? error.message : undefined);
    },
  });

  // El orden lo decide el módulo con `sequence`; a igual secuencia, el alfabético
  // deja la botonera estable entre recargas.
  const todos = (spec.reports ?? [])
    .slice()
    .sort((a, b) => (a.sequence ?? 10) - (b.sequence ?? 10) || a.label.localeCompare(b.label, 'es'));

  const reportes = todos.filter((reporte) => reporte.enabled !== false);

  // Los que el servidor apagó, con su motivo ya redactado. Mismo criterio que
  // las acciones: el botón muerto no se dibuja, la explicación sí existe.
  const ocultas: AccionNoDisponible[] = todos
    .filter((reporte) => reporte.enabled === false)
    .map((reporte) => ({
      clave: `reporte-${reporte.name}`,
      etiqueta: reporte.label,
      motivo: motivoDelServidor(reporte.disabled_reason, reporte.label),
    }));

  const enCurso = imprimir.isPending ? imprimir.variables.reporte : undefined;

  /** Con parámetros, primero el diálogo; sin ellos, se descarga derecho. */
  const lanzar = (reporte: ModelReport): void => {
    if ((reporte.params ?? []).length > 0) {
      setEnDialogo(reporte);
      return;
    }
    imprimir.mutate({ reporte, params: {} });
  };

  const items: ToolbarItem[] = reportes.map((reporte) => {
    // Lo deshabilitado por el servidor ya se filtró: acá sólo puede frenar
    // la pantalla misma.
    const ayuda = reporte.help !== undefined && reporte.help !== '' ? reporte.help : null;
    return {
      key: `reporte-${reporte.name}`,
      label: reporte.label,
      variant: 'secondary' as const,
      disabled: blockedReason !== null,
      loading: enCurso?.name === reporte.name,
      title: ayuda,
      reason: blockedReason === null ? null : `${blockedReason} ${DETALLE_SUCIO}`,
      // Un reporte es una LECTURA: arma un PDF con lo que hay en la base y no
      // escribe nada ni le manda nada a nadie (eso es «Enviar por email», que es
      // otro botón). Encadenar el guardado con la impresión es seguro, y es
      // justo lo que la persona quería cuando apretó.
      salida:
        blockedReason === null
          ? null
          : salidaDeGuardado(guardado, {
              continuar: () => {
                lanzar(reporte);
              },
              encadena: true,
            }),
      onClick: () => {
        lanzar(reporte);
      },
    };
  });

  const dialogs = (
    <ActionDialog
      action={enDialogo === null ? null : comoAccion(enDialogo)}
      count={1}
      loading={imprimir.isPending}
      onConfirm={(params) => {
        if (enDialogo !== null) imprimir.mutate({ reporte: enDialogo, params });
      }}
      onCancel={() => {
        setEnDialogo(null);
      }}
    />
  );

  return { items, dialogs, ocultas };
}

/** La barra de siempre. Ver el comentario gemelo en `ActionBar`. */
export function ReportBar({ spec, recordId, size = 'md', blockedReason = null }: ReportBarProps) {
  const { items, dialogs } = useReportItems({ spec, recordId, blockedReason });
  return (
    <>
      <Toolbar items={items} visible={items.length} size={size} />
      {dialogs}
    </>
  );
}
